import type { IListReviewItem } from '@/features/products/types/review'
import type { IReviewSummary } from '@/features/products/services/reviews'
import { BRAND_NAME, SCHEMA_IDS } from './constants'
import { buildReviewItem, filterPublishedReviews } from './review'

type BuildProductSchemaParams = {
  product: Product
  url: string
  reviews?: IListReviewItem[]
  summary?: IReviewSummary | null
}

const oneYearFromNow = (): string =>
  new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0]

export const buildProductSchema = ({ product, url, reviews = [], summary }: BuildProductSchemaParams) => {
  const finalPrice =
    product.price_range?.minimum_price?.final_price?.value ??
    product.price_range?.minimum_price?.regular_price?.value ??
    0

  const description =
    product.meta_description ||
    (product.short_description?.html
      ? product.short_description.html.replace(/<[^>]*>/g, '').trim()
      : product.name)

  const publishedReviews = filterPublishedReviews(reviews)
  const reviewItems = publishedReviews.map(buildReviewItem)

  const aggregateRating = (() => {
    if (summary && summary.total_review > 0 && summary.avg_rating > 0) {
      return {
        '@type': 'AggregateRating',
        ratingValue: Number(summary.avg_rating).toFixed(1),
        reviewCount: summary.total_review,
        bestRating: 5,
        worstRating: 1,
      }
    }
    if (product.rating_summary > 0 && product.review_count > 0) {
      return {
        '@type': 'AggregateRating',
        ratingValue: (product.rating_summary / 20).toFixed(1),
        reviewCount: product.review_count,
        bestRating: 5,
        worstRating: 1,
      }
    }
    return undefined
  })()

  const schema: Record<string, any> = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    image: product.image?.url,
    description,
    brand: { '@type': 'Brand', name: BRAND_NAME },
    sku: product.sku,
    mpn: product.sku,
    category: product.categories?.[0]?.name,
    offers: {
      '@type': 'Offer',
      url,
      priceCurrency: 'VND',
      price: Number(finalPrice).toFixed(0),
      priceValidUntil: oneYearFromNow(),
      availability:
        product.stock_status === 'IN_STOCK'
          ? 'https://schema.org/InStock'
          : 'https://schema.org/OutOfStock',
      itemCondition: 'https://schema.org/NewCondition',
      seller: { '@id': SCHEMA_IDS.organization },
    },
  }

  if (aggregateRating) schema.aggregateRating = aggregateRating
  if (reviewItems.length > 0) schema.review = reviewItems

  return schema
}
